import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { safeNextPath } from '@/lib/next-path';
import { landingPath } from '@/features/overview/overview-rows';
import { useAuthStore, type AuthUser, type OrganisationSummary, type OrgSummary } from '@/stores/auth';
import { Button, Card, CardContent, Input, FintMark } from '@/features/common/ui';

interface LoginResponse {
  user?: AuthUser;
  orgs?: OrgSummary[];
  organisations?: OrganisationSummary[];
  mfaRequired?: boolean;
}

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  /**
   * Where to go afterwards, if something sent us here mid-flow.
   *
   * Today that is only `/v1/oauth/authorize` bouncing a signed-out browser
   * through login on its way to the consent screen. `safeNextPath` matches the
   * value against a list of known routes and returns undefined for anything
   * else - an unchecked `next` here is an open redirect under Fint's own
   * domain, which is the one thing that makes a phishing link look genuine.
   */
  const next = safeNextPath(params.get('next'));

  const login = useMutation({
    mutationFn: () => api.post<LoginResponse>('/auth/login', { email, password }),
    onSuccess: (data) => {
      if (data.mfaRequired) {
        // `next` is deliberately NOT carried here. There is no `/mfa` route in
        // this SPA yet - the catch-all swallows it - so threading a parameter
        // through it would be writing for a screen that does not exist. When
        // that screen is built it should carry `next` on, and this comment is
        // where whoever builds it will look.
        navigate('/mfa');
        return;
      }
      if (data.user && data.orgs) setSession(data.user, data.orgs, data.organisations);
      // The previous session's cache, if any, belongs to a different person.
      queryClient.clear();
      // A flow that sent us here wins; otherwise the Overview for a person
      // with several businesses, and the dashboard for a person with one.
      navigate(next ?? landingPath(data.orgs?.length ?? 0));
    },
  });

  const error = login.error instanceof ApiError ? login.error : undefined;

  return (
    <AuthShell title="Sign in to Fint" subtitle="Invoicing, expenses and bank accounts in one place.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          login.mutate();
        }}
      >
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error?.fieldError('email')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error?.fieldError('password')}
        />

        {/* A failure with no field attached - a wrong password, or a 429. */}
        {error && !error.errors?.length ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error.message}
          </p>
        ) : null}

        <Button type="submit" className="w-full" loading={login.isPending}>
          Sign in
        </Button>
      </form>

      <div className="mt-4 flex items-center justify-between text-sm">
        <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground">
          Forgot password?
        </Link>
        <Link to="/register" className="font-medium text-primary hover:underline">
          Create an account
        </Link>
      </div>
    </AuthShell>
  );
}

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-muted/30 px-4 py-10">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-6 text-center">
          <p className="flex items-center justify-center gap-2 text-2xl font-semibold tracking-tight text-primary">
            <FintMark className="size-9" />
            Fint
          </p>
        </div>
        <Card>
          <CardContent className="pt-5">
            <div className="mb-5 space-y-1">
              <h1 className="text-lg font-semibold">{title}</h1>
              {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
            </div>
            {children}
          </CardContent>
        </Card>
        <p className="mt-4 flex justify-center gap-4 text-xs text-muted-foreground">
          <Link to="/privacy" className="inline-flex touch:min-h-11 items-center hover:text-foreground">
            Privacy
          </Link>
          <Link to="/terms" className="inline-flex touch:min-h-11 items-center hover:text-foreground">
            Terms
          </Link>
        </p>
      </div>
    </div>
  );
}
